import {
  Activity,
  AlertTriangle,
  BarChart3,
  Brain,
  Building,
  Building2,
  Coins,
  ChevronDown,
  ClipboardList,
  FileBarChart,
  FileText,
  FolderTree,
  GraduationCap,
  Key,
  LayoutDashboard,
  Library,
  LineChart,
  Link2,
  Plug2,
  Mail,
  Award,
  Medal,
  MessageSquareText,
  PieChart,
  Settings, Shield,
  Sparkles,
  Tags,
  Ticket,
  TrendingUp,
  Trophy,
  UserCheck,
  UserCircle,
  UserPlus,
  Users,
  Webhook,
  Clipboard,
  Route,
  Workflow,
  Crown,
  History,
  FileArchive,
  UploadCloud
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  PERMISSION_LIST_KEY,
  DASHBOARD_PERMISSION,
  COURSE_CATALOG_PERMISSION,
  COURSE_GENERATION_PERMISSION,
  SCORM_IMPORT_PERMISSION,
  AVATAR_LIBRARY_PERMISSION,
  CATEGORY_PERMISSION,
  TRENDING_TOPICS_PERMISSION,
  COURSE_ASSIGNMENT_PERMISSION,
  COURSE_ASSIGNMENT_ANALYTICS_PERMISSION,
  CERTIFICATION_CATALOG_PERMISSION,
  CERTIFICATION_CREATION_PERMISSION,
  CERTIFICATION_ASSIGNMENT_PERMISSION,
  CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION,
  LEARNING_PATH_CATALOG_PERMISSION,
  LEARNING_PATH_CREATION_PERMISSION,
  LEARNING_PATH_WORKFLOW_PERMISSION,
  LEARNING_PATH_ASSIGNMENT_PERMISSION,
  LEARNING_PATH_ASSIGNMENT_ANALYTICS_PERMISSION,
  USERS_PERMISSION,
  INVITE_MANAGEMENT_PERMISSION,
  GROUPS_PERMISSION,
  ROLES_PERMISSION,
  ANALYTICS_OVERVIEW_PERMISSION,
  EMPLOYEE_PROGRESS_PERMISSION,
  MY_TEAM_PROGRESS_PERMISSION,
  DEPARTMENT_PROGRESS_PERMISSION,
  ROLE_PROGRESS_PERMISSION,
  COURSE_PERFORMANCE_PERMISSION,
  LEARNING_PATH_PERFORMANCE_PERMISSION,
  CERTIFICATION_PERFORMANCE_PERMISSION,
  ACTIVITY_REPORT_PERMISSION,
  LEADERBOARD_PERMISSION,
  BADGE_MANAGEMENT_PERMISSION,
  USER_ACHIEVEMENTS_PERMISSION,
  REVIEWS_PERMISSION,
  DOCUMENT_STORE_PERMISSION,
  BUSINESS_SETTINGS_PERMISSION,
  TICKET_REQUESTS_PERMISSION,
  WEBHOOK_SETTINGS_PERMISSION,
  AI_MODEL_SETTINGS_PERMISSION,
  SSO_CONFIGURATION_PERMISSION,
  SCIM_CONFIGURATION_PERMISSION,
  LTI_INTEGRATION_PERMISSION,
  EMAIL_LOGS_PERMISSION,
  EMAIL_TEMPLATE_PERMISSION,
  CERTIFICATE_TEMPLATE_PERMISSION,
  TRAINING_STATUS_REPORT_PERMISSION,
  AI_COST_TRACKING_PERMISSION,
  AI_USAGE_PERMISSION,
  AUDIT_TRAIL_PERMISSION,
} from '@/constants/constant-permissions';
import {
  ADMIN_DASHBOARD,
  ADMIN_COURSE_CATALOG,
  ADMIN_COURSE_ADD,
  ADMIN_SCORM_UPLOAD,
  ADMIN_AVATAR_LIBRARY,
  ADMIN_COURSE_CATEGORIES,
  ADMIN_REPORTS_TRENDING,
  ADMIN_COURSE_ASSIGNMENT,
  ADMIN_COURSE_ASSIGNMENT_ANALYTICS,
  ADMIN_CERTIFICATIONS,
  ADMIN_CERTIFICATION_CREATE,
  ADMIN_CERTIFICATION_ASSIGNMENT,
  ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS,
  ADMIN_LEARNING_PATHS,
  ADMIN_LEARNING_PATH_CREATE,
  ADMIN_LP_WORKFLOW_LIST,
  ADMIN_LEARNING_PATH_ASSIGNMENT,
  ADMIN_LEARNING_PATH_ASSIGNMENT_ANALYTICS,
  ADMIN_USERS,
  ADMIN_USERS_INVITATIONS,
  ADMIN_USERS_DEPARTMENTS,
  ADMIN_ROLES,
  ADMIN_ANALYTICS,
  ADMIN_TRAINING_STATUS_REPORT,
  ADMIN_ANALYTICS_EMPLOYEE_PROGRESS,
  ADMIN_ANALYTICS_MY_TEAM_PROGRESS,
  ADMIN_ANALYTICS_DEPARTMENT_PROGRESS,
  ADMIN_ANALYTICS_ROLE_PROGRESS,
  ADMIN_ANALYTICS_COURSE_PERFORMANCE,
  ADMIN_ANALYTICS_LEARNING_PATH_PERFORMANCE,
  ADMIN_ANALYTICS_CERTIFICATION_PERFORMANCE,
  ADMIN_REPORTS_ACTIVITY,
  ADMIN_EMAIL_LOGS,
  ADMIN_REPORTS_AUDIT_TRAIL,
  ADMIN_REPORTS_TENANT,
  ADMIN_REPORTS_AI_COST,
  ADMIN_AI_USAGE,
  ADMIN_LEADERBOARD,
  ADMIN_GAMIFICATION_BADGES,
  ADMIN_GAMIFICATION_USER_ACHIEVEMENTS,
  ADMIN_REVIEWS,
  ADMIN_KNOWLEDGE_DOCUMENTS,
  ADMIN_SETTINGS_BUSINESS,
  ADMIN_SETTINGS_TICKET_REQUESTS,
  ADMIN_SETTINGS_WEBHOOKS,
  ADMIN_SETTINGS_AI_MODELS,
  ADMIN_TENANTS,
  ADMIN_CREDIT_PLANS,
  ADMIN_SSO_CONFIGURATION,
  ADMIN_SCIM_CONFIGURATION,
  ADMIN_LTI_INTEGRATIONS,
  ADMIN_EMAIL_TEMPLATES,
  ADMIN_CERTIFICATE_TEMPLATES,
} from '@/constants/constant-routes';

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
  hasNotification?: boolean;
  permissionKeys?: string[];
  children?: MenuItem[];
}

export const menuItems: MenuItem[] = [
  {
    title: 'Dashboard',
    icon: <LayoutDashboard className="w-5 h-5 text-blue-600" />,
    path: ADMIN_DASHBOARD,
    permissionKeys: [DASHBOARD_PERMISSION.DASHBOARD_READ],
  },
  {
    title: 'Courses',
    icon: <GraduationCap className="w-5 h-5 text-purple-600" />,
    path: '/course',
    children: [
      {
        title: 'All Courses',
        icon: <Library className="w-4 h-4 text-purple-500" />,
        path: ADMIN_COURSE_CATALOG,
        permissionKeys: [COURSE_CATALOG_PERMISSION.COURSE_CATALOG_READ, COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE],
      },
      {
        title: 'AI Course Builder',
        icon: <Sparkles className="w-4 h-4 text-yellow-500" />,
        path: ADMIN_COURSE_ADD,
        permissionKeys: [COURSE_GENERATION_PERMISSION.COURSE_GENERATION_READ, COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE],
      },
      {
        title: 'Import SCORM',
        icon: <UploadCloud className="w-4 h-4 text-orange-500" />,
        path: ADMIN_SCORM_UPLOAD,
        permissionKeys: [SCORM_IMPORT_PERMISSION.SCORM_IMPORT_READ, SCORM_IMPORT_PERMISSION.SCORM_IMPORT_WRITE],
      },
      {
        title: 'Avatar Library',
        icon: <UserCircle className="w-4 h-4 text-pink-500" />,
        path: ADMIN_AVATAR_LIBRARY,
        permissionKeys: [AVATAR_LIBRARY_PERMISSION.AVATAR_LIBRARY_READ, AVATAR_LIBRARY_PERMISSION.AVATAR_LIBRARY_WRITE],
      },
      {
        title: 'Categories',
        icon: <Tags className="w-4 h-4 text-violet-500" />,
        path: ADMIN_COURSE_CATEGORIES,
        permissionKeys: [CATEGORY_PERMISSION.CATEGORY_READ, CATEGORY_PERMISSION.CATEGORY_WRITE],
      },
      {
        title: 'Trending Topics',
        icon: <TrendingUp className="w-4 h-4 text-green-500" />,
        path: ADMIN_REPORTS_TRENDING,
        permissionKeys: [TRENDING_TOPICS_PERMISSION.TRENDING_TOPICS_READ, TRENDING_TOPICS_PERMISSION.TRENDING_TOPICS_WRITE],
      },
    ],
  },
  {
    title: 'Course Assignment',
    icon: <ClipboardList className="w-5 h-5 text-indigo-600" />,
    path: '/course-assignment',
    children: [
      {
        title: 'Assign Courses',
        icon: <UserPlus className="w-4 h-4 text-indigo-500" />,
        path: ADMIN_COURSE_ASSIGNMENT,
        permissionKeys: [COURSE_ASSIGNMENT_PERMISSION.COURSE_ASSIGNMENT_READ, COURSE_ASSIGNMENT_PERMISSION.COURSE_ASSIGNMENT_WRITE],
      },
      {
        title: 'Manage Assignments',
        icon: <BarChart3 className="w-4 h-4 text-purple-500" />,
        path: ADMIN_COURSE_ASSIGNMENT_ANALYTICS,
        permissionKeys: [COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_READ, COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_WRITE],
      },
    ],
  },
  {
    title: 'Certifications',
    icon: <Award className="w-5 h-5 text-amber-600" />,
    path: '/certifications',
    children: [
      {
        title: 'All Certifications',
        icon: <Medal className="w-4 h-4 text-amber-500" />,
        path: ADMIN_CERTIFICATIONS,
        permissionKeys: [CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_READ, CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_WRITE],
      },
      {
        title: 'AI Certification Builder',
        icon: <Sparkles className="w-4 h-4 text-purple-500" />,
        path: ADMIN_CERTIFICATION_CREATE,
        permissionKeys: [CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_READ, CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_WRITE],
      },
    ],
  },
  {
    title: 'Certification Assignment',
    icon: <Clipboard className="w-5 h-5 text-yellow-600" />,
    path: '/certification-assignment',
    children: [
      {
        title: 'Assign Certification',
        icon: <UserPlus className="w-4 h-4 text-indigo-500" />,
        path: ADMIN_CERTIFICATION_ASSIGNMENT,
        permissionKeys: [CERTIFICATION_ASSIGNMENT_PERMISSION.CERTIFICATION_ASSIGNMENT_READ, CERTIFICATION_ASSIGNMENT_PERMISSION.CERTIFICATION_ASSIGNMENT_WRITE],
      },
      {
        title: 'Manage Assignments',
        icon: <BarChart3 className="w-4 h-4 text-purple-500" />,
        path: ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS,
        permissionKeys: [CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_READ, CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_WRITE],
      },
    ],
  },
  {
    title: 'Learning Paths',
    icon: <Route className="w-5 h-5 text-purple-600" />,
    path: '/learning-paths',
    children: [
      {
        title: 'All Learning Paths',
        icon: <Route className="w-4 h-4 text-purple-500" />,
        path: ADMIN_LEARNING_PATHS,
        permissionKeys: [LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_READ, LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE],
      },
      {
        title: 'AI Path Builder',
        icon: <Sparkles className="w-4 h-4 text-pink-500" />,
        path: ADMIN_LEARNING_PATH_CREATE,
        permissionKeys: [LEARNING_PATH_CREATION_PERMISSION.ADMIN_LEARNING_PATH_CREATION_READ, LEARNING_PATH_CREATION_PERMISSION.ADMIN_LEARNING_PATH_CREATION_WRITE],
      },
      {
        title: 'Corporate Assignment Workflow',
        icon: <Workflow className="w-4 h-4 text-blue-500" />,
        path: ADMIN_LP_WORKFLOW_LIST,
        permissionKeys: [LEARNING_PATH_WORKFLOW_PERMISSION.ADMIN_LEARNING_PATH_WORKFLOW_READ, LEARNING_PATH_WORKFLOW_PERMISSION.ADMIN_LEARNING_PATH_WORKFLOW_WRITE],
      },
    ],
  },
  {
    title: 'Learning Path Assignment',
    icon: <FolderTree className="w-5 h-5 text-green-600" />,
    path: '/learning-path-assignment',
    children: [
      {
        title: 'Assign Learning Path',
        icon: <UserPlus className="w-4 h-4 text-green-500" />,
        path: ADMIN_LEARNING_PATH_ASSIGNMENT,
        permissionKeys: [LEARNING_PATH_ASSIGNMENT_PERMISSION.ADMIN_LEARNING_PATH_ASSIGNMENT_READ, LEARNING_PATH_ASSIGNMENT_PERMISSION.ADMIN_LEARNING_PATH_ASSIGNMENT_WRITE],
      },
      {
        title: 'Manage Assignments',
        icon: <BarChart3 className="w-4 h-4 text-emerald-500" />,
        path: ADMIN_LEARNING_PATH_ASSIGNMENT_ANALYTICS,
        permissionKeys: [LEARNING_PATH_ASSIGNMENT_ANALYTICS_PERMISSION.ADMIN_LEARNING_PATH_ASSIGNMENT_ANALYTICS_READ, LEARNING_PATH_ASSIGNMENT_ANALYTICS_PERMISSION.ADMIN_LEARNING_PATH_ASSIGNMENT_ANALYTICS_WRITE],
      },
    ],
  },
  {
    title: 'User Management',
    icon: <Users className="w-5 h-5 text-cyan-600" />,
    path: '/users',
    children: [
      {
        title: 'Employees',
        icon: <UserCheck className="w-4 h-4 text-cyan-500" />,
        path: ADMIN_USERS,
        permissionKeys: [USERS_PERMISSION.ADMIN_USERS_READ, USERS_PERMISSION.ADMIN_USERS_WRITE],
      },
      {
        title: 'Invitations',
        icon: <Mail className="w-4 h-4 text-blue-500" />,
        path: ADMIN_USERS_INVITATIONS,
        permissionKeys: [INVITE_MANAGEMENT_PERMISSION.ADMIN_INVITE_MANAGEMENT_READ, INVITE_MANAGEMENT_PERMISSION.ADMIN_INVITE_MANAGEMENT_WRITE],
      },
      {
        title: 'Departments',
        icon: <FolderTree className="w-4 h-4 text-emerald-500" />,
        path: ADMIN_USERS_DEPARTMENTS,
        permissionKeys: [GROUPS_PERMISSION.ADMIN_GROUPS_READ, GROUPS_PERMISSION.ADMIN_GROUPS_WRITE],
      },
      {
        title: 'Roles',
        icon: <Shield className="w-4 h-4 text-amber-500" />,
        path: ADMIN_ROLES,
        permissionKeys: [ROLES_PERMISSION.ADMIN_ROLES_READ, ROLES_PERMISSION.ADMIN_ROLES_WRITE],
      },
    ],
  },
  {
    title: 'Reports & Analytics',
    icon: <BarChart3 className="w-5 h-5 text-green-600" />,
    path: '/analytics',
    children: [
      {
        title: 'Overview',
        icon: <PieChart className="w-4 h-4 text-green-500" />,
        path: ADMIN_ANALYTICS,
        permissionKeys: [ANALYTICS_OVERVIEW_PERMISSION.ADMIN_COURSE_ANALYTICS_READ, ANALYTICS_OVERVIEW_PERMISSION.ADMIN_CERTIFICATION_ANALYTICS_READ],
      },
      {
        title: 'Employee Progress',
        icon: <LineChart className="w-4 h-4 text-blue-500" />,
        path: ADMIN_ANALYTICS_EMPLOYEE_PROGRESS,
        permissionKeys: [EMPLOYEE_PROGRESS_PERMISSION.ADMIN_EMPLOYEE_PROGRESS_READ, EMPLOYEE_PROGRESS_PERMISSION.ADMIN_EMPLOYEE_PROGRESS_WRITE],
      },
      {
        title: 'My Team Progress',
        icon: <Users className="w-4 h-4 text-cyan-500" />,
        path: ADMIN_ANALYTICS_MY_TEAM_PROGRESS,
        permissionKeys: [MY_TEAM_PROGRESS_PERMISSION.ADMIN_MY_TEAM_PROGRESS_READ, MY_TEAM_PROGRESS_PERMISSION.ADMIN_MY_TEAM_PROGRESS_WRITE],
      },
      {
        title: 'Department Progress',
        icon: <Building className="w-4 h-4 text-indigo-500" />,
        path: ADMIN_ANALYTICS_DEPARTMENT_PROGRESS,
        permissionKeys: [DEPARTMENT_PROGRESS_PERMISSION.ADMIN_DEPARTMENT_PROGRESS_READ, DEPARTMENT_PROGRESS_PERMISSION.ADMIN_DEPARTMENT_PROGRESS_WRITE],
      },
      {
        title: 'Role Progress',
        icon: <Shield className="w-4 h-4 text-indigo-500" />,
        path: ADMIN_ANALYTICS_ROLE_PROGRESS,
        permissionKeys: [ROLE_PROGRESS_PERMISSION.ADMIN_ROLE_PROGRESS_READ, ROLE_PROGRESS_PERMISSION.ADMIN_ROLE_PROGRESS_WRITE],
      },
      {
        title: 'Course Performance',
        icon: <FileBarChart className="w-4 h-4 text-purple-500" />,
        path: ADMIN_ANALYTICS_COURSE_PERFORMANCE,
        permissionKeys: [COURSE_PERFORMANCE_PERMISSION.ADMIN_COURSE_PERFORMANCE_READ, COURSE_PERFORMANCE_PERMISSION.ADMIN_COURSE_PERFORMANCE_WRITE],
      },
      {
        title: 'Certification Performance',
        icon: <Award className="w-4 h-4 text-amber-500" />,
        path: ADMIN_ANALYTICS_CERTIFICATION_PERFORMANCE,
        permissionKeys: [CERTIFICATION_PERFORMANCE_PERMISSION.ADMIN_CERTIFICATION_PERFORMANCE_READ, CERTIFICATION_PERFORMANCE_PERMISSION.ADMIN_CERTIFICATION_PERFORMANCE_WRITE],
      },
      {
        title: 'Learning Path Performance',
        icon: <Route className="w-4 h-4 text-teal-500" />,
        path: ADMIN_ANALYTICS_LEARNING_PATH_PERFORMANCE,
        permissionKeys: [LEARNING_PATH_PERFORMANCE_PERMISSION.ADMIN_LEARNING_PATH_PERFORMANCE_READ, LEARNING_PATH_PERFORMANCE_PERMISSION.ADMIN_LEARNING_PATH_PERFORMANCE_WRITE],
      },
      {
        title: 'Activity Report',
        icon: <Activity className="w-4 h-4 text-violet-500" />,
        path: ADMIN_REPORTS_ACTIVITY,
        permissionKeys: [ACTIVITY_REPORT_PERMISSION.ADMIN_ACTIVITY_REPORT_READ, ACTIVITY_REPORT_PERMISSION.ADMIN_ACTIVITY_REPORT_WRITE],
      },
      {
        // Tenant Reports — management-scoped usage reports.
        // Hidden on non-management hosts via the isManagementTenant filter below.
        title: 'Tenant Reports',
        icon: <FileBarChart className="w-4 h-4 text-purple-500" />,
        path: ADMIN_REPORTS_TENANT,
      },
      {
        // AI Cost Tracking — cross-tenant cost dashboard. Three gates: (1) management-only,
        // hidden on non-management hosts via the isManagementTenant filter below; (2) role
        // permission (assign admin_ai_cost_tracking_read/_write in the DB); (3) backend
        // assertManagement() on every endpoint. Route is wrapped in <ManagementOnlyRoute>.
        title: 'AI Cost Tracking',
        icon: <FileBarChart className="w-4 h-4 text-emerald-500" />,
        path: ADMIN_REPORTS_AI_COST,
        permissionKeys: [AI_COST_TRACKING_PERMISSION.ADMIN_AI_COST_TRACKING_READ, AI_COST_TRACKING_PERMISSION.ADMIN_AI_COST_TRACKING_WRITE],
      },
      {
        // AI Usage — tenant-scoped (each tenant sees its OWN AI usage). Not management-only;
        // gated by the admin_ai_usage_read/_write role permission (assign in the DB).
        title: 'AI Usage',
        icon: <Coins className="w-4 h-4 text-violet-500" />,
        path: ADMIN_AI_USAGE,
        permissionKeys: [AI_USAGE_PERMISSION.ADMIN_AI_USAGE_READ, AI_USAGE_PERMISSION.ADMIN_AI_USAGE_WRITE],
      },
      {
        title: 'Training Status Report',
        icon: <FileBarChart className="w-4 h-4 text-indigo-600" />,
        path: ADMIN_TRAINING_STATUS_REPORT,
        permissionKeys: [TRAINING_STATUS_REPORT_PERMISSION.ADMIN_TRAINING_STATUS_REPORT_READ, TRAINING_STATUS_REPORT_PERMISSION.ADMIN_TRAINING_STATUS_REPORT_WRITE],
      },
      {
        title: 'Email Logs',
        icon: <Mail className="w-4 h-4 text-violet-500" />,
        path: ADMIN_EMAIL_LOGS,
        permissionKeys: [EMAIL_LOGS_PERMISSION.ADMIN_EMAIL_LOGS_READ],
      },
      {
        title: 'Audit Trail Report',
        icon: <History className="w-4 h-4 text-rose-500" />,
        path: ADMIN_REPORTS_AUDIT_TRAIL,
        permissionKeys: [
          AUDIT_TRAIL_PERMISSION.ADMIN_AUDIT_TRAIL_READ,
          AUDIT_TRAIL_PERMISSION.ADMIN_AUDIT_TRAIL_WRITE,
        ],
      },
    ],
  },
  {
    title: 'Gamification',
    icon: <Crown className="w-5 h-5 text-amber-600" />,
    path: '/gamification',
    children: [
      {
        title: 'Leaderboard',
        icon: <Trophy className="w-5 h-5 text-yellow-600" />,
        path: ADMIN_LEADERBOARD,
        permissionKeys: [LEADERBOARD_PERMISSION.ADMIN_LEADERBOARD_READ],
      },
      {
        title: 'Badge Management',
        icon: <Medal className="w-5 h-5 text-rose-600" />,
        path: ADMIN_GAMIFICATION_BADGES,
        permissionKeys: [BADGE_MANAGEMENT_PERMISSION.ADMIN_BADGE_MANAGEMENT_READ, BADGE_MANAGEMENT_PERMISSION.ADMIN_BADGE_MANAGEMENT_WRITE],
      },
      {
        title: 'Learner Achievements',
        icon: <Award className="w-5 h-5 text-teal-600" />,
        path: ADMIN_GAMIFICATION_USER_ACHIEVEMENTS,
        permissionKeys: [USER_ACHIEVEMENTS_PERMISSION.ADMIN_USER_ACHIEVEMENTS_READ],
      },
    ],
  },
  {
    title: 'Reviews',
    icon: <MessageSquareText className="w-5 h-5 text-violet-600" />,
    path: ADMIN_REVIEWS,
    permissionKeys: [REVIEWS_PERMISSION.ADMIN_REVIEWS_MANAGEMENT_READ, REVIEWS_PERMISSION.ADMIN_REVIEWS_MANAGEMENT_WRITE],
  },
  {
    title: 'Templates',
    icon: <FileText className="w-5 h-5 text-purple-600" />,
    path: '/templates',
    children: [
      {
        title: 'Email Templates',
        icon: <Mail className="w-4 h-4 text-purple-500" />,
        path: ADMIN_EMAIL_TEMPLATES,
        permissionKeys: [EMAIL_TEMPLATE_PERMISSION.ADMIN_EMAIL_TEMPLATE_READ, EMAIL_TEMPLATE_PERMISSION.ADMIN_EMAIL_TEMPLATE_WRITE],
      },
      {
        title: 'Certificate Templates',
        icon: <Award className="w-4 h-4 text-amber-500" />,
        path: ADMIN_CERTIFICATE_TEMPLATES,
        permissionKeys: [CERTIFICATE_TEMPLATE_PERMISSION.ADMIN_CERTIFICATE_TEMPLATE_READ, CERTIFICATE_TEMPLATE_PERMISSION.ADMIN_CERTIFICATE_TEMPLATE_WRITE],
      },
    ],
  },

  {
    title: 'Knowledge Store',
    icon: <Brain className="w-5 h-5 text-pink-600" />,
    path: '/knowledge',
    children: [
      {
        title: 'Documents',
        icon: <FileText className="w-4 h-4 text-pink-500" />,
        path: ADMIN_KNOWLEDGE_DOCUMENTS,
        permissionKeys: [DOCUMENT_STORE_PERMISSION.ADMIN_DOCUMENT_STORE_READ, DOCUMENT_STORE_PERMISSION.ADMIN_DOCUMENT_STORE_WRITE],
      },
    ],
  },
  {
    title: 'Settings',
    icon: <Settings className="w-5 h-5 text-gray-600" />,
    path: '/settings',
    children: [
      {
        title: 'Business',
        icon: <Building2 className="w-4 h-4 text-slate-500" />,
        path: ADMIN_SETTINGS_BUSINESS,
        permissionKeys: [BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_READ, BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_WRITE],
      },
      {
        title: 'Ticket Requests',
        icon: <Ticket className="w-4 h-4 text-violet-500" />,
        path: ADMIN_SETTINGS_TICKET_REQUESTS,
        permissionKeys: [TICKET_REQUESTS_PERMISSION.ADMIN_TICKET_REQUESTS_READ, TICKET_REQUESTS_PERMISSION.ADMIN_TICKET_REQUESTS_WRITE],
      },
      {
        title: 'Webhooks',
        icon: <Webhook className="w-4 h-4 text-blue-500" />,
        path: ADMIN_SETTINGS_WEBHOOKS,
        permissionKeys: [WEBHOOK_SETTINGS_PERMISSION.ADMIN_WEBHOOK_SETTINGS_READ, WEBHOOK_SETTINGS_PERMISSION.ADMIN_WEBHOOK_SETTINGS_WRITE],
      },
      {
        title: 'AI Model Settings',
        icon: <Brain className="w-4 h-4 text-indigo-500" />,
        path: ADMIN_SETTINGS_AI_MODELS,
        permissionKeys: [AI_MODEL_SETTINGS_PERMISSION.ADMIN_AI_MODEL_SETTINGS_READ, AI_MODEL_SETTINGS_PERMISSION.ADMIN_AI_MODEL_SETTINGS_WRITE],
      },
    ],
  },
  {
    title: 'Enterprise',
    icon: <Building className="w-5 h-5 text-rose-600" />,
    path: '/enterprise',
    children: [
      { title: 'Tenants', icon: <Building2 className="w-4 h-4 text-blue-500" />, path: ADMIN_TENANTS },
      { title: 'Credit Plans', icon: <Coins className="w-4 h-4 text-amber-500" />, path: ADMIN_CREDIT_PLANS },
      {
        title: 'SSO Config',
        icon: <Key className="w-4 h-4 text-rose-500" />,
        path: ADMIN_SSO_CONFIGURATION,
        permissionKeys: [SSO_CONFIGURATION_PERMISSION.ADMIN_SSO_CONFIGURATION_READ, SSO_CONFIGURATION_PERMISSION.ADMIN_SSO_CONFIGURATION_WRITE],
      },
      {
        title: 'SCIM Config',
        icon: <Link2 className="w-4 h-4 text-indigo-500" />,
        path: ADMIN_SCIM_CONFIGURATION,
        permissionKeys: [SCIM_CONFIGURATION_PERMISSION.ADMIN_SCIM_CONFIGURATION_READ, SCIM_CONFIGURATION_PERMISSION.ADMIN_SCIM_CONFIGURATION_WRITE],
      },
      {
        title: 'LTI Integrations',
        icon: <Plug2 className="w-4 h-4 text-purple-500" />,
        path: ADMIN_LTI_INTEGRATIONS,
        permissionKeys: [LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_READ, LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_WRITE],
      },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
  gradientEnabled?: boolean;
}

export default function Sidebar({ isOpen, isCollapsed, onToggle, onClose, gradientEnabled = true }: SidebarProps) {
  const location = useLocation();
  const { isManagementTenant } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  // Filter menu items based on user permissions and tenant type
  const filteredMenuItems = useMemo(() => {
    const userPermissions: string[] = JSON.parse(
      sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]',
    );

    const hasAnyPermission = (permissionKeys?: string[]) => {
      if (!permissionKeys || permissionKeys.length === 0) return true;
      return permissionKeys.some((key) => userPermissions.includes(key));
    };

    return menuItems
      .map((item) => {
        if (item.children) {
          // Filter children by permissions
          let filteredChildren = item.children.filter((child) =>
            hasAnyPermission(child.permissionKeys),
          );

          // For Enterprise section, hide management-only items (Tenants, Credit Plans) for non-management tenants
          if (item.title === 'Enterprise' && !isManagementTenant) {
            filteredChildren = filteredChildren.filter(
              (child) => child.title !== 'Tenants' && child.title !== 'Credit Plans',
            );
          }

          // Tenant Reports and AI Cost Tracking are management-scoped cross-tenant
          // reports — hide them on non-management hosts. (AI Cost Tracking is
          // additionally gated by a role permission via its permissionKeys above.)
          if (item.title === 'Reports & Analytics' && !isManagementTenant) {
            filteredChildren = filteredChildren.filter(
              (child) => child.title !== 'AI Cost Tracking' && child.title !== 'Tenant Reports',
            );
          }

          // Hide parent if no visible children remain
          if (filteredChildren.length === 0) return null;

          return { ...item, children: filteredChildren };
        }

        // For items without children, check their own permissions
        if (!hasAnyPermission(item.permissionKeys)) return null;

        return item;
      })
      .filter(Boolean) as MenuItem[];
  }, [isManagementTenant]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleMenu = (title: string) => {
    setExpandedMenus(prev =>
      prev.includes(title)
        ? [] // Close if clicking the same menu
        : [title] // Only keep the clicked menu open
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: MenuItem) => {
    if (location.pathname.startsWith(item.path)) return true;
    return item.children?.some(child => isActive(child.path));
  };

  return (
    <nav className="flex-1 overflow-y-auto custom-scrollbar p-4" style={{ overflow: isCollapsed ? 'visible' : 'auto' }}>
      <ul className="space-y-1">
        {filteredMenuItems.map((item, index) => {
          const handleMouseEnter = () => {
            // Only apply hover behavior on desktop when sidebar is collapsed
            if (isCollapsed && isDesktop) {
              // Clear any existing timeout
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
              setHoveredMenu(item.title);
            }
          };

          const handleMouseLeave = (e: React.MouseEvent) => {
            // Only apply hover behavior on desktop when sidebar is collapsed
            if (isCollapsed && isDesktop) {
              const relatedTarget = e.relatedTarget as HTMLElement;
              // Check if mouse is moving to the dropdown or another menu item
              if (!relatedTarget?.closest('.dropdown-menu') && !relatedTarget?.closest('.menu-item')) {
                hoverTimeoutRef.current = setTimeout(() => {
                  setHoveredMenu(null);
                }, 100);
              }
            }
          };

          return (
            <li key={item.path} className="relative menu-item"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}>
              {item.children ? (
                <>
                  <div
                    onClick={() => toggleMenu(item.title)}
                    className={`w-full flex items-center justify-between px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition-all cursor-pointer group ${isParentActive(item)
                      ? gradientEnabled
                        ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 text-primary-700 shadow-sm'
                        : 'bg-primary-50 text-primary-700'
                      : gradientEnabled
                        ? 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/30'
                        : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    title={isCollapsed && isDesktop ? item.title : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`relative ${isParentActive(item) ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'}`}>
                        {item.icon}
                        {item.hasNotification && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                        )}
                      </span>
                      <span className={`text-xs lg:text-sm font-medium ${isCollapsed ? 'lg:hidden' : ''}`}>{item.title}</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedMenus.includes(item.title) ? 'rotate-180' : ''
                      } ${isCollapsed ? 'hidden lg:hidden' : ''}`} />
                  </div>

                  {/* Expanded submenu - show normally on mobile/tablet, hide on desktop when collapsed */}
                  {expandedMenus.includes(item.title) && (!isCollapsed || !isDesktop) && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={() => {
                              // Close mobile sidebar when item is clicked
                              if (!isDesktop && onClose) {
                                onClose();
                              }
                            }}
                            className={`flex items-center justify-between px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg transition-all group ${isActive(child.path)
                              ? gradientEnabled
                                ? 'bg-gradient-to-r from-primary-50/70 to-primary-100/40 text-primary-700 font-medium'
                                : 'bg-primary-50 text-primary-700 font-medium'
                              : gradientEnabled
                                ? 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50/50 hover:to-transparent'
                                : 'text-gray-600 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-3">
                                <span className={`relative ${isActive(child.path) ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'}`}>
                                  {child.icon}
                                  {child.hasNotification && (
                                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                                  )}
                                </span>
                                <span className="text-xs lg:text-sm">{child.title}</span>
                              </div>
                              {child.badge && (
                                <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                                  {child.badge}
                                </span>
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Flyout submenu for collapsed sidebar - only on desktop */}
                  {isCollapsed && hoveredMenu === item.title && isDesktop && (
                    <>
                      {/* Invisible bridge to maintain hover */}
                      <div className="absolute left-full top-0 w-4 h-full" />
                      <div className="dropdown-menu absolute left-[calc(100%+4px)] top-0 w-56 bg-white rounded-lg shadow-xl border py-2 z-[9999]"
                        onMouseEnter={() => {
                          if (hoverTimeoutRef.current) {
                            clearTimeout(hoverTimeoutRef.current);
                            hoverTimeoutRef.current = null;
                          }
                          setHoveredMenu(item.title);
                        }}
                        onMouseLeave={() => {
                          hoverTimeoutRef.current = setTimeout(() => setHoveredMenu(null), 100);
                        }}>
                        <div className="px-3 py-2 border-b mb-1">
                          <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                        </div>
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center justify-between px-3 py-2 mx-2 rounded-lg transition-colors ${isActive(child.path)
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="relative">
                                {child.icon}
                                {child.hasNotification && (
                                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                                )}
                              </span>
                              <span className="text-sm">{child.title}</span>
                            </div>
                            {child.badge && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                                {child.badge}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => {
                    // Close mobile sidebar when item is clicked
                    if (!isDesktop && onClose) {
                      onClose();
                    }
                  }}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all group ${isActive(item.path)
                    ? gradientEnabled
                      ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 text-primary-700 font-medium shadow-sm'
                      : 'bg-primary-50 text-primary-700 font-medium'
                    : gradientEnabled
                      ? 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/30'
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  title={isCollapsed && isDesktop ? item.title : undefined}
                >
                  <div className="flex items-center gap-3">
                    <span className={isActive(item.path) ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'}>
                      {item.icon}
                    </span>
                    <span className={`text-sm font-medium ${isCollapsed ? 'lg:hidden' : ''}`}>{item.title}</span>
                  </div>

                  {/* Flyout label for collapsed sidebar on single items - only on desktop */}
                  {isCollapsed && hoveredMenu === item.title && isDesktop && (
                    <>
                      {/* Invisible bridge to maintain hover */}
                      <div className="absolute left-full top-0 w-4 h-full" />
                      <div className="dropdown-menu absolute left-[calc(100%+4px)] top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-[9999] whitespace-nowrap"
                        onMouseEnter={() => {
                          if (hoverTimeoutRef.current) {
                            clearTimeout(hoverTimeoutRef.current);
                            hoverTimeoutRef.current = null;
                          }
                          setHoveredMenu(item.title);
                        }}
                        onMouseLeave={() => {
                          hoverTimeoutRef.current = setTimeout(() => setHoveredMenu(null), 100);
                        }}>
                        {item.title}
                      </div>
                    </>
                  )}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
